import React, { useEffect, useMemo, useState } from 'react'
import { Grid, Loader2, Search } from 'lucide-react'
import Modal from '../../../../components/ERP/Modal/Modal'
import { productGridService } from '../../../../services/erpApi'
import './GradeProdutosPage.css'

interface ProductGrid {
  id: number
  name: string
  base_name?: string
  description?: string
  product_count?: number
}

function GradeProdutosPage() {
  const [grids, setGrids] = useState<ProductGrid[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [selectedGrid, setSelectedGrid] = useState<ProductGrid | null>(null)
  const [description, setDescription] = useState('')
  const [baseName, setBaseName] = useState('')

  useEffect(() => {
    const loadGrids = async () => {
      try {
        setLoading(true)
        setError('')
        const response = await productGridService.getAll()
        if (response?.success) {
          setGrids(response.data || [])
        } else {
          setError(response?.message || 'Nao foi possivel carregar as grades.')
        }
      } catch (err) {
        console.error('Erro ao carregar grades:', err)
        setError('Nao foi possivel carregar as grades.')
      } finally {
        setLoading(false)
      }
    }

    loadGrids()
  }, [])

  const handleOpenDescriptionModal = (grid: ProductGrid) => {
    setSelectedGrid(grid)
    setDescription(grid.description || '')
    setBaseName(grid.base_name || '')
    setModalOpen(true)
  }

  const handleCloseModal = () => {
    if (saving) return
    setModalOpen(false)
    setSelectedGrid(null)
    setDescription('')
    setBaseName('')
  }

  const handleSaveDescription = async () => {
    if (!selectedGrid) return
    try {
      setSaving(true)
      const payload = {
        description: description.trim() ? description.trim() : null,
        baseName: baseName.trim() ? baseName.trim() : null
      }
      const response = await productGridService.update(selectedGrid.id, payload)
      if (response?.success) {
        setGrids((prev) =>
          prev.map((grid) =>
            grid.id === selectedGrid.id
              ? { 
                  ...grid, 
                  description: payload.description || undefined,
                  base_name: payload.baseName || undefined
                }
              : grid
          )
        )
        handleCloseModal()
      } else {
        setError(response?.message || 'Nao foi possivel salvar as alteracoes.')
      }
    } catch (err) {
      console.error('Erro ao salvar alteracoes:', err)
      setError('Nao foi possivel salvar as alteracoes.')
    } finally {
      setSaving(false)
    }
  }

  const filteredGrids = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    if (!term) return grids
    return grids.filter((grid) => {
      const name = (grid.name || '').toLowerCase()
      const baseName = (grid.base_name || '').toLowerCase()
      const description = (grid.description || '').toLowerCase()
      return (
        name.includes(term) ||
        baseName.includes(term) ||
        description.includes(term)
      )
    })
  }, [grids, searchTerm])

  return (
    <div className="grade-produtos-page">
      <div className="grade-produtos-header">
        <div>
          <h1 className="grade-produtos-title">
            <Grid size={22} />
            Grade de produtos
          </h1>
          <p className="grade-produtos-subtitle">
            Lista de produtos virtuais com a identificacao da grade.
          </p>
        </div>

        <div className="grade-produtos-search">
          <Search size={16} />
          <input
            type="text"
            placeholder="Buscar por grade, produto virtual..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </div>
      </div>

      <div className="grade-produtos-card">
        {loading && (
          <div className="grade-produtos-loading">
            <Loader2 className="spinner" size={18} />
            Carregando grades...
          </div>
        )}

        {!loading && error && (
          <div className="grade-produtos-error">{error}</div>
        )}

        {!loading && !error && filteredGrids.length === 0 && (
          <div className="grade-produtos-empty">Nenhuma grade encontrada.</div>
        )}

        {!loading && !error && filteredGrids.length > 0 && (
          <div className="grade-produtos-table-wrapper">
            <table className="grade-produtos-table">
              <thead>
                <tr>
                  <th>Produto virtual</th>
                  <th>Grade</th>
                  <th>Produtos</th>
                  <th>Acoes</th>
                </tr>
              </thead>
              <tbody>
                {filteredGrids.map((grid) => (
                  <tr key={grid.id}>
                    <td>
                      <div className="grade-produtos-name">
                        {grid.base_name || 'Sem nome base'}
                      </div>
                    </td>
                    <td>{grid.name}</td>
                    <td>{grid.product_count ?? 0}</td>
                    <td>
                      <button
                        type="button"
                        className="grade-produtos-action"
                        onClick={() => handleOpenDescriptionModal(grid)}
                      >
                        Editar descricao
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        title="Editar grade"
        size="large"
        footer={
          <>
            <button
              className="btn-secondary"
              type="button"
              onClick={handleCloseModal}
              disabled={saving}
            >
              Cancelar
            </button>
            <button
              className="btn-primary"
              type="button"
              onClick={handleSaveDescription}
              disabled={saving}
            >
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </>
        }
      >
        <div className="grade-produtos-modal">
          <div className="grade-produtos-modal-header-field">
            <label htmlFor="nome-produto-virtual">Produto virtual</label>
            <input
              id="nome-produto-virtual"
              type="text"
              value={baseName}
              onChange={(event) => setBaseName(event.target.value)}
              placeholder="Digite o nome do produto virtual"
              className="grade-produtos-input"
              disabled={saving}
            />
          </div>
          
          <div className="grade-produtos-modal-field">
            <label htmlFor="descricao-grade">Descricao</label>
            <textarea
              id="descricao-grade"
              rows={10}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Digite a descricao da grade"
              className="grade-produtos-textarea"
              disabled={saving}
            />
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default GradeProdutosPage
